import { useCallback, useEffect, useState, useRef } from 'react';
import { Session } from '@ais-ui/auth';
import { useLocalization } from '@ais-ui/locale-management';
import { useToast } from '@ais-ui/ui-core';
import {
  getCsvVesselTrackResult,
  getQueueStatusOfCsvVesselTrack,
  queueCsvVesselTrack,
} from './downloadTrackEndpoints';

export type DownloadStatus =
  | 'idle'
  | 'queued'
  | 'downloading'
  | 'success'
  | 'error';

interface DownloadState {
  status: DownloadStatus;
  error: string | null;
  progress: number;
  data: {
    content: string;
    filename: string;
  } | null;
}

interface UseDownloadTrackResult {
  downloadState: DownloadState;
  startDownload: (mmsi: number) => Promise<void>;
  reset: () => void;
}

const POLLING_INTERVAL = 1000; // 1 second

export const useDownloadTrack = (session: Session): UseDownloadTrackResult => {
  const { toast } = useToast();
  const { messages } = useLocalization();
  const downloadedRef = useRef(false);

  const [downloadState, setDownloadState] = useState<DownloadState>({
    status: 'idle',
    error: null,
    progress: 0,
    data: null,
  });

  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(
    null
  );

  const clearPollingInterval = useCallback(() => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  }, [pollingInterval]);

  const reset = useCallback(() => {
    clearPollingInterval();
    downloadedRef.current = false;
    setDownloadState({
      status: 'idle',
      error: null,
      progress: 0,
      data: null,
    });
  }, [clearPollingInterval]);

  const handleError = useCallback(
    (error: Error) => {
      clearPollingInterval();
      setDownloadState((prev) => ({
        ...prev,
        status: 'error',
        error: error.message,
      }));

      toast({
        title: messages.genericToastTitleError,
        description: error.message,
        variant: 'destructive',
      });
    },
    [clearPollingInterval, toast, messages]
  );

  const triggerDownload = useCallback(
    (filename: string, base64Data: string) => {
      if (downloadedRef.current) {
        return; // Prevent multiple downloads
      }

      // Decode the base64 data
      const decodedData = atob(base64Data);

      // Create blob and trigger download
      const blob = new Blob([decodedData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);

      downloadedRef.current = true;
    },
    []
  );

  const pollStatus = useCallback(
    async (queueUuid: string) => {
      try {
        const result = await getQueueStatusOfCsvVesselTrack(session, queueUuid);

        setDownloadState((prev) => ({
          ...prev,
          progress: result.percentComplete,
        }));

        if (result.errorMessage) {
          throw new Error(result.errorMessage);
        }

        if (result.status === 'QueueComplete') {
          clearPollingInterval();
          const downloadResult = await getCsvVesselTrackResult(
            session,
            queueUuid
          );

          triggerDownload(downloadResult.filename, downloadResult.data);

          setDownloadState((prev) => ({
            ...prev,
            status: 'success',
            data: {
              content: downloadResult.data,
              filename: downloadResult.filename,
            },
          }));

          toast({
            title: messages.genericToastTitleSuccess,
            description: 'Successfully downloaded track',
          });
          return;
        }

        return result.status;
      } catch (error) {
        handleError(error as Error);
      }
    },
    [
      session,
      clearPollingInterval,
      triggerDownload,
      toast,
      messages,
      handleError,
    ]
  );

  const startDownload = useCallback(
    async (mmsi: number) => {
      try {
        reset();
        setDownloadState((prev) => ({ ...prev, status: 'queued' }));

        toast({
          title: messages.genericToastTitleStarted,
          description: 'Started download of track',
        });

        const { queueUuid } = await queueCsvVesselTrack(session, mmsi);
        setDownloadState((prev) => ({ ...prev, status: 'downloading' }));

        const interval = setInterval(async () => {
          const status = await pollStatus(queueUuid);
          if (status !== 'QueuePending') {
            clearInterval(interval);
          }
        }, POLLING_INTERVAL);
        setPollingInterval(interval);
      } catch (error) {
        handleError(error as Error);
      }
    },
    [session, reset, pollStatus, handleError, toast, messages]
  );

  useEffect(() => {
    return () => {
      clearPollingInterval();
    };
  }, [clearPollingInterval]);

  return {
    downloadState,
    startDownload,
    reset,
  };
};
